import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  type CouponCode,
  EnhancedTicketInfo,
  getTicketAndExtraTicketIds,
  getTicketPrice,
  SplitCartCoupon,
  useTicketDownloadUrl,
  useTicketingEventId,
  useTicketsCart,
  useTicketsEvent,
  useTicketsInfo,
  useTicketsRemoveCartItem,
} from 'restful/tickets';

import { isCartItemOutOfStock } from './utils';

export const useTicketingEvent = () => {
  const { eventId, timeslotId } = useTicketingEventId();
  const { data: ticketsEvent } = useTicketsEvent(
    { id: eventId, timeslotId },
    { enabled: !!eventId },
  );

  const ticketIds = useMemo(
    () => getTicketAndExtraTicketIds(ticketsEvent),
    [ticketsEvent],
  );

  return { event: ticketsEvent, eventId, ticketIds };
};

const useTicketsInfoLookup = () => {
  const { ticketIds, eventId } = useTicketingEvent();
  const { info } = useTicketsInfo({ eventId, ticketIds });

  const lookup = useMemo(
    () =>
      info?.reduce<Record<string, EnhancedTicketInfo>>(
        (acc, { ticketId, price, ...info }) => {
          acc[ticketId] = {
            isSoldOut: !info.isAvailable,
            ...getTicketPrice(price),
            ...info,
          };
          return acc;
        },
        {},
      ) ?? {},
    [info],
  );

  return { lookup };
};

export const useTicketInfoLookup = (): ((
  ticketId: string,
) => EnhancedTicketInfo) => {
  const { lookup } = useTicketsInfoLookup();
  return useCallback((ticketId: string) => lookup[ticketId] ?? {}, [lookup]);
};

export const useTicketInfo = (ticketId: string): EnhancedTicketInfo => {
  const { lookup } = useTicketsInfoLookup();
  return lookup[ticketId] ?? {};
};

export const useCouponDiscounts = (couponCodes?: Array<CouponCode>) => {
  if (!couponCodes?.length) return;
  const { coupon, saudiRewards } = couponCodes.reduce<SplitCartCoupon>(
    (memo, coupon) => {
      const updateKey =
        coupon.couponType === 'LoyaltyCoupon' ? 'saudiRewards' : 'coupon';
      memo[updateKey] = {
        code: coupon.code,
        value: {
          amount: coupon?.discountValue?.amount ?? 0,
          currency: coupon?.discountValue?.currency ?? '',
        },
      };
      return memo;
    },
    {
      coupon: undefined,
      saudiRewards: undefined,
    },
  );

  return {
    coupon,
    saudiRewards,
  };
};

export const useCartOutOfStockItems = () => {
  const { cart } = useTicketsCart();
  const { removeCartItem } = useTicketsRemoveCartItem();

  const outOfStockCartItems =
    cart?.items?.filter((item) => isCartItemOutOfStock(item)) ?? [];

  const handleRemoveOutOfStockCartItems = () => {
    const itemIds = outOfStockCartItems.map(({ id }) => id);
    removeCartItem({ itemIds });
  };

  return {
    hasOutOfStockCartItems: !!outOfStockCartItems.length,
    outOfStockCartItems,
    onRemoveOutOfStockCartItems: handleRemoveOutOfStockCartItems,
  };
};

export const useDownloadTicket = () => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const { requestTicketDownloadUrl, isLoading } = useTicketDownloadUrl();

  const handleDownloadTicket = (ticketId: string) => {
    requestTicketDownloadUrl(
      {
        ticketId,
      },
      {
        onSuccess: (data) => {
          const downloadUrl = data.ticketDownloadUrl?.link;
          if (downloadUrl) {
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            // add setTimeout to fix safari issue
            setTimeout(() => {
              link.click();
              document.body.removeChild(link);
            }, 100);
          }
        },
        onError: () => {
          enqueueSnackbar(
            intl.formatMessage(
              { id: 'account.tickets.download.error' },
              { variant: 'error' },
            ),
          );
        },
      },
    );
  };

  return { handleDownloadTicket, isLoading };
};
